import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetPosCatalogResponse } from '@/api/handlers/resources/pos/responses';
import { PosQueryKeys } from './posQueryKeys';

/**
 * How often the till re-reads the catalog on its own. Other sale points in the
 * same branch are selling from the same shelf, so a figure a minute old is
 * already a guess — this is the ceiling on how wrong it can get while nobody
 * touches this terminal.
 */
export const POS_CATALOG_REFRESH_MS = 60_000;

/**
 * Reads the whole sellable catalog for the signed-in branch from
 * `GET /pos/catalog`.
 *
 * Unlike the older hooks here, this one **unwraps** the `ApiResponse` envelope
 * and throws the failure (`.agents/technical.md` §10: the query library must
 * observe failures, not successful error-data). `ErrorResponse` already extends
 * `Error`, so the service's normalized failure is the typed error. `data` is
 * therefore the catalog itself, and a refusal surfaces as `isError`.
 *
 * Stock is the reason this refreshes on a schedule rather than being cached.
 * The catalog goes stale for four reasons and each has its own trigger: this
 * terminal sold something or was refused (the sale mutation and the payment
 * flow invalidate `PosQueryKeys.catalog()`), the seller came back to the window
 * (`refetchOnWindowFocus`), or another sale point in the branch sold something
 * (`refetchInterval`). `staleTime: 0` is what lets the first two actually
 * refetch rather than read a cached page.
 */
export const usePosCatalogQuery = (enabled = true) => {
    const { apiClient } = useApi();

    return useQuery<GetPosCatalogResponse>({
        queryKey: PosQueryKeys.catalog(),
        queryFn: async () => {
            const result = await apiClient.pos.getCatalog();
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        enabled,
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchInterval: POS_CATALOG_REFRESH_MS,
    });
};
